import { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Calendar, Flag,
  Sparkles, RefreshCw, Dice5, Swords, Timer, LifeBuoy, ShieldAlert, Gauge,
  Menu, X, Search, Pin, PinOff, ChevronDown, ChevronRight, History, ArrowLeftRight,
  Archive, AlertTriangle, HeartPulse,
} from "lucide-react";
import {
  Stats, Players, WebUsers, Vip, VipRenewals, Economy, EconomyTransfers,
  GamblingLedger, PvpLedger, RewardsEditor, CooldownsEditor, EconomySettings,
  BuilderPricing, PalGameSettings, Kits, Give, Giveaways, DailyVipStreaks,
  WelcomeReset, ScheduleAdmin, Reports, Tickets, ServerControl, AutoRestart,
  ServerConfig, PermAdmins, WhitelistAdmin, Bans, Audit, Markers, Moderation,
  ServerSettings, AdminRaids, MarkerRenamer, WorldTransfer, BotArchives, PalAudit, EggPools,
  LinkHealth,
} from "@/components/admin";
import AdminRaidsManual from "./AdminRaidsManual";
import AdminRaidsAutomatic from "./AdminRaidsAutomatic";

/* ---------- Tab definitions, grouped for the new shell ---------- */
const GROUPS = [
  {
    id: "overview",
    label: "Overview",
    tabs: [{ to: "", label: "Stats", icon: Shield, end: true, keywords: "dashboard home" }],
  },
  {
    id: "users",
    label: "Users & Mod",
    tabs: [
      { to: "players", label: "Players", icon: Users, keywords: "accounts coins balance" },
      { to: "web-users", label: "Web Users", icon: UserCog, keywords: "discord login oauth" },
      { to: "bans", label: "Bans", icon: Ban },
      { to: "moderation", label: "Moderation", icon: ShieldAlert, keywords: "kicks mutes warnings" },
      { to: "pal-audit", label: "Pal Audit", icon: AlertTriangle, keywords: "cheat pals level ivs souls flagged review" },
      { to: "reports", label: "Reports", icon: Flag },
      { to: "tickets", label: "Tickets", icon: LifeBuoy, keywords: "support help" },
    ],
  },
  {
    id: "vip",
    label: "VIP & Rewards",
    tabs: [
      { to: "vip", label: "VIP", icon: Crown },
      { to: "vip-renewals", label: "VIP Renewals", icon: RefreshCw, keywords: "expire monthly" },
      { to: "rewards", label: "Daily / VIP Rewards", icon: Gift },
      { to: "daily", label: "Daily / VIP Streaks", icon: Sparkles, keywords: "claim streak" },
      { to: "welcome", label: "Welcome Reset", icon: RefreshCw, keywords: "first time" },
      { to: "giveaways", label: "Giveaways", icon: Gift },
    ],
  },
  {
    id: "economy",
    label: "Economy",
    tabs: [
      { to: "economy", label: "Economy", icon: Coins, keywords: "currency coins" },
      { to: "transfers", label: "Transfers", icon: ScrollText, keywords: "send pay" },
      { to: "gambling-log", label: "Gambling Log", icon: Dice5, keywords: "casino bets" },
      { to: "pvp-log", label: "PvP Audit", icon: Swords, keywords: "duels arena" },
      { to: "settings", label: "Econ Settings", icon: Settings, keywords: "shop prices" },
      { to: "builder-pricing", label: "Builder Pricing", icon: Coins, keywords: "pal cost passives" },
    ],
  },
  {
    id: "game",
    label: "Game",
    tabs: [
      { to: "palgame", label: "Pal Game", icon: Layers, keywords: "battle minigame" },
      { to: "shop", label: "Shop & Kits", icon: PackageOpen },
      { to: "give", label: "Give", icon: Wrench, keywords: "grant items currency" },
      { to: "raids", label: "Raid Bosses", icon: Swords, keywords: "boss alpha" },
      { to: "raids/manual", label: "Manual Raids", icon: Swords, keywords: "summon profile template prizes" },
      { to: "raids/automatic", label: "Automatic Raids", icon: Swords, keywords: "scheduled profile template prizes" },
      { to: "egg-pools", label: "Egg Pools", icon: PackageOpen, keywords: "egg eggs hatch pal pool prize shop kit giveegg" },
      { to: "cooldowns", label: "Cooldowns", icon: Timer },
    ],
  },
  {
    id: "server",
    label: "Server Ops",
    tabs: [
      { to: "server", label: "Server", icon: Server, keywords: "rcon restart" },
      { to: "server-settings", label: "Server Settings", icon: Gauge },
      { to: "autorestart", label: "Auto-Restart", icon: RefreshCw },
      { to: "server-config", label: "Server Config", icon: Settings },
      { to: "perm-admins", label: "In-game Admins", icon: KeyRound, keywords: "permissions" },
      { to: "whitelist", label: "Whitelist", icon: Lock },
      { to: "world-transfer", label: "World Transfer", icon: ArrowLeftRight, keywords: "move player base pal editor save sftp migrate" },
      { to: "bot-archives", label: "Bot Archives", icon: Archive, keywords: "zip download backup bot update secrets" },
      { to: "link-health", label: "Link Health", icon: HeartPulse, keywords: "link linked players legacy relink migrate nudge dm" },
    ],
  },
  {
    id: "map",
    label: "Map & Logs",
    tabs: [
      { to: "markers", label: "Map Markers", icon: MapIcon },
      { to: "rename-markers", label: "Rename Fast Travels", icon: MapIcon, keywords: "ft fasttravel" },
      { to: "schedule", label: "Schedule", icon: Calendar },
      { to: "audit", label: "Audit Trail", icon: ScrollText, keywords: "history log" },
    ],
  },
];

/** Flat list (preserve definition order) for search / recents / pins. */
const ALL_TABS = GROUPS.flatMap((g) => g.tabs.map((t) => ({ ...t, group: g.label })));
const tabKey = (t) => t.to || "stats";

const PIN_KEY = "admin.pinned";
const RECENT_KEY = "admin.recents";
const COLLAPSED_KEY = "admin.collapsedGroups";

function loadLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

/* ---------- The page ---------- */
export default function Admin() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pinned, setPinned] = useState(() => loadLS(PIN_KEY, []));
  const [recents, setRecents] = useState(() => loadLS(RECENT_KEY, []));
  const [collapsed, setCollapsed] = useState(() => loadLS(COLLAPSED_KEY, {}));
  const searchRef = useRef(null);

  // Track recent tabs as the user navigates.
  useEffect(() => {
    const slug = location.pathname.replace(/^\/admin\/?/, "") || "";
    const key = slug || "stats";
    const found = ALL_TABS.find((t) => tabKey(t) === key);
    if (!found) return;
    setRecents((prev) => {
      const next = [key, ...prev.filter((k) => k !== key)].slice(0, 5);
      saveLS(RECENT_KEY, next);
      return next;
    });
  }, [location.pathname]);

  // Cmd+K / Ctrl+K to focus search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const togglePin = (key) => {
    setPinned((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      saveLS(PIN_KEY, next);
      return next;
    });
  };
  const toggleGroup = (id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveLS(COLLAPSED_KEY, next);
      return next;
    });
  };

  // Compute filtered groups based on the search query.
  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!q) return GROUPS;
    return GROUPS
      .map((g) => ({
        ...g,
        tabs: g.tabs.filter((t) =>
          t.label.toLowerCase().includes(q) ||
          (t.keywords || "").toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.tabs.length > 0);
  }, [q]);

  const pinnedTabs = pinned
    .map((k) => ALL_TABS.find((t) => tabKey(t) === k))
    .filter(Boolean);
  const recentTabs = recents
    .map((k) => ALL_TABS.find((t) => tabKey(t) === k))
    .filter(Boolean)
    .slice(0, 4);

  // Active tab label for the mobile header.
  const activeSlug = location.pathname.replace(/^\/admin\/?/, "") || "";
  const activeTab = ALL_TABS.find((t) => tabKey(t) === (activeSlug || "stats"));

  /* ---------- Nav body — shared between desktop sidebar & mobile drawer.
     Rendered as a function rather than a single element because BOTH
     copies live in the DOM at once (the desktop aside is `hidden lg:block`,
     not unmounted). Attaching `searchRef` to both meant React handed the
     ref to whichever mounted last, so ⌘K could focus the invisible one. */
  const renderNavBody = (variant) => (
    <div className="flex flex-col gap-4" data-testid="admin-nav-body">
      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          ref={variant === "desktop" ? searchRef : null}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tabs…  (⌘K)"
          data-testid="admin-search-input"
          // text-base (16px) on phones so iOS doesn't zoom the page on
          // focus; text-sm restored from lg upward.
          className="w-full h-11 lg:h-auto bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-md py-2 pl-9 pr-3 text-base lg:text-sm text-[var(--bone)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--teal)] transition-colors"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--bone)]"
            data-testid="admin-search-clear"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Pinned */}
      {!q && pinnedTabs.length > 0 && (
        <NavGroup label="Pinned" icon={Pin}>
          {pinnedTabs.map((t) => (
            <NavRow key={`pin-${tabKey(t)}`} tab={t} pinned onTogglePin={togglePin} />
          ))}
        </NavGroup>
      )}

      {/* Recents */}
      {!q && recentTabs.length > 1 && (
        <NavGroup label="Recent" icon={History}>
          {recentTabs.map((t) => (
            <NavRow
              key={`rec-${tabKey(t)}`}
              tab={t}
              pinned={pinned.includes(tabKey(t))}
              onTogglePin={togglePin}
            />
          ))}
        </NavGroup>
      )}

      {/* Grouped tabs */}
      {filteredGroups.map((g) => {
        const isCollapsed = !q && collapsed[g.id];
        return (
          <div key={g.id} data-testid={`admin-group-${g.id}`}>
            <button
              type="button"
              onClick={() => toggleGroup(g.id)}
              className="w-full flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mb-1 px-1 min-h-9 lg:min-h-0 hover:text-[var(--bone)] transition-colors"
              data-testid={`admin-group-toggle-${g.id}`}
            >
              <span>{g.label}</span>
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            </button>
            {!isCollapsed && (
              <ul className="flex flex-col gap-0.5">
                {g.tabs.map((t) => (
                  <NavRow
                    key={tabKey(t)}
                    tab={t}
                    pinned={pinned.includes(tabKey(t))}
                    onTogglePin={togglePin}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {q && filteredGroups.length === 0 && (
        <div className="text-xs text-[var(--muted)] italic px-2 py-3 text-center">
          No tabs match “{q}”.
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="admin-page">
      <PageHeader
        title="Admin Crypt"
        subtitle={activeTab ? `${activeTab.group} → ${activeTab.label}` : "The keys to the kingdom."}
        right={
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[rgba(255,255,255,0.1)] text-xs uppercase tracking-wider text-[var(--bone)] bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)] active:scale-[0.98] transition"
            data-testid="admin-menu-button"
            aria-label="Open admin menu"
          >
            <Menu size={14} /> Menu
          </button>
        }
      />

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Desktop sidebar */}
        <aside
          className="hidden lg:block glass-card p-3 lg:sticky lg:top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto"
          data-testid="admin-sidebar-desktop"
        >
          {renderNavBody("desktop")}
        </aside>

        {/* Main content */}
        <div className="min-w-0">
          <Routes>
            <Route index element={<Stats />} />
            <Route path="players" element={<Players />} />
            <Route path="web-users" element={<WebUsers />} />
            <Route path="vip" element={<Vip />} />
            <Route path="economy" element={<Economy />} />
            <Route path="transfers" element={<EconomyTransfers />} />
            <Route path="gambling-log" element={<GamblingLedger />} />
            <Route path="pvp-log" element={<PvpLedger />} />
            <Route path="rewards" element={<RewardsEditor />} />
            <Route path="cooldowns" element={<CooldownsEditor />} />
            <Route path="settings" element={<EconomySettings />} />
            <Route path="builder-pricing" element={<BuilderPricing />} />
            <Route path="palgame" element={<PalGameSettings />} />
            <Route path="shop" element={<Kits />} />
            <Route path="kits" element={<Navigate to="/admin/shop" replace />} />
            <Route path="give" element={<Give />} />
            <Route path="giveaways" element={<Giveaways />} />
            <Route path="server" element={<ServerControl />} />
            <Route path="server-settings" element={<ServerSettings />} />
            <Route path="autorestart" element={<AutoRestart />} />
            <Route path="server-config" element={<ServerConfig />} />
            <Route path="perm-admins" element={<PermAdmins />} />
            <Route path="whitelist" element={<WhitelistAdmin />} />
            <Route path="world-transfer" element={<WorldTransfer />} />
            <Route path="bot-archives" element={<BotArchives />} />
            <Route path="link-health" element={<LinkHealth />} />
            <Route path="vip-renewals" element={<VipRenewals />} />
            <Route path="daily" element={<DailyVipStreaks />} />
            <Route path="welcome" element={<WelcomeReset />} />
            <Route path="schedule" element={<ScheduleAdmin />} />
            <Route path="reports" element={<Reports />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="bans" element={<Bans />} />
            <Route path="moderation/*" element={<Moderation />} />
            <Route path="pal-audit" element={<PalAudit />} />
            <Route path="audit" element={<Audit />} />
            <Route path="markers" element={<Markers />} />
            <Route path="rename-markers" element={<MarkerRenamer />} />
            <Route path="raids" element={<AdminRaids />} />
            <Route path="raids/manual" element={<AdminRaidsManual />} />
            <Route path="raids/automatic" element={<AdminRaidsAutomatic />} />
            <Route path="egg-pools" element={<EggPools />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Routes>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          data-testid="admin-mobile-drawer"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in"
            onClick={() => setMobileOpen(false)}
            data-testid="admin-drawer-backdrop"
          />
          <div className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-[#0c0f14] border-r border-[rgba(255,255,255,0.08)] shadow-2xl overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(255,255,255,0.06)] sticky top-0 bg-[#0c0f14]/95 backdrop-blur z-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">Admin</div>
                <div className="text-sm font-semibold text-[var(--bone)]">Sections</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded hover:bg-white/5 active:scale-95 transition"
                aria-label="Close admin menu"
                data-testid="admin-drawer-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3 flex-1">{renderNavBody("mobile")}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Small presentational pieces ---------- */

function NavGroup({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mb-1 px-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

function NavRow({ tab, pinned, onTogglePin }) {
  return (
    <li className="group relative">
      <NavLink
        to={`/admin${tab.to ? "/" + tab.to : ""}`}
        end={tab.end}
        data-testid={`admin-nav-${tab.to || "stats"}`}
        className={({ isActive }) =>
          // pr-12 reserves the pin's lane so the link and the pin never
          // share a hit area — on touch the pin is always visible, so
          // there is no invisible control sitting on top of the label.
          `flex items-center gap-2 pl-3 pr-12 min-h-11 lg:min-h-0 py-2 rounded-md text-xs tracking-wider transition-colors ${
            isActive
              ? "bg-[rgba(0,229,255,0.12)] text-[var(--teal)] border border-[rgba(0,229,255,0.25)]"
              : "text-[var(--muted)] hover:text-[var(--bone)] hover:bg-white/[0.03] border border-transparent"
          }`
        }
      >
        <tab.icon size={14} className="shrink-0" />
        <span className="truncate flex-1">{tab.label}</span>
      </NavLink>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTogglePin(tabKey(tab)); }}
        // The unpinned state used to be `opacity-0 group-hover:opacity-60`.
        // `group-hover` never fires on touch, but an opacity-0 button still
        // receives taps — so every row had an invisible ~20px hit zone on
        // its right edge that silently pinned instead of navigating.
        // Now: faintly visible with a real 44px target on touch, and the
        // original hover-reveal behaviour on desktop.
        className={`absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 lg:h-auto lg:w-auto lg:right-2 lg:p-1 rounded transition-opacity ${
          pinned
            ? "opacity-100 text-amber-300"
            : "opacity-40 lg:opacity-0 lg:group-hover:opacity-60 text-[var(--muted)] hover:text-amber-300"
        }`}
        aria-label={pinned ? `Unpin ${tab.label}` : `Pin ${tab.label}`}
        aria-pressed={pinned}
        data-testid={`admin-pin-${tab.to || "stats"}`}
      >
        {pinned ? <Pin size={14} fill="currentColor" /> : <PinOff size={14} />}
      </button>
    </li>
  );
}
